import { PointCloudOctree } from '@pnext/three-loader';
// PointCloudWalkAbortError is re-exported below for callsite compatibility,
// not used inside this module — see the `export { ... as ... }` near the
// top. Importing it as a value would trigger TS6133.
import { PointCloudWalkError, walkPointCloud } from '@/lib/pointcloud/walkPointCloud';
import {
  buildSegments,
  PolylinePoint,
  polylineXyAabb,
  projectPointToPolyline,
  totalLength,
} from './polylineMath';

/**
 * Elevation-profile sampler — thin wrapper around `walkPointCloud`.
 *
 * The shared walker handles octree traversal, on-demand node loading,
 * bounded-parallel bulk loads, chunked iteration, progress reporting,
 * and abort. This module is just the elevation-profile-specific bits:
 *
 *   - Region predicate: node's world AABB intersects the polyline AABB
 *     expanded by `buffer` on every side.
 *   - Per-point predicate: perpendicular XY distance to the polyline ≤ buffer.
 *   - Output: `(chainage, elevation)` pairs sorted by chainage, then
 *     stride-decimated to ≤ `maxChartSamples` so the chart stays snappy.
 *
 * No grid bucketing — a profile chart is 1-D and the user cares about the
 * full vertical scatter, not aggregated-per-cell tops.
 */

export interface ComputeProfileInput {
  /** Polyline vertices (XYZ, project SRID, Z-up). At least 2 vertices. */
  polyline: PolylinePoint[];
  /** Half-width of the corridor around the polyline, metres. */
  buffer: number;
  /** Source point cloud — walked directly, no rendering involved. */
  pco: PointCloudOctree;
  /** Two-phase progress callback. */
  onProgress?: (phase: 'loading' | 'sampling', done: number, total: number) => void;
  /** Cancellation. */
  signal?: AbortSignal;
  /** Cap before downsample. Defaults to 2 000 000 — bigger truncates. */
  maxRawPoints?: number;
  /** How many chart samples to keep after stride decimation. Defaults to 5000. */
  maxChartSamples?: number;
  /** Concurrent loader.load calls. Defaults to 6. */
  maxConcurrentLoads?: number;
  /**
   * Stop descending into nodes whose octree spacing is finer than this.
   * Defaults to `buffer / 2` — for a 1 m buffer that's 0.5 m spacing, plenty
   * to resolve a profile without loading the full point cloud at max LOD.
   */
  targetSpacing?: number;
}

export interface ComputeProfileResult {
  /** (chainage, elevation) pairs, sorted by chainage, downsampled. */
  samples: Array<{ chainage: number; elevation: number }>;
  /** Raw count before downsample. */
  rawPointCount: number;
  /** Total polyline XY length (metres). */
  totalLength: number;
  /** How many octree leaf nodes were sampled. Telemetry. */
  nodesUsed: number;
  durationMs: number;
}

/** Re-exported for backward compatibility — panel error handlers catch this. */
export { PointCloudWalkAbortError as ElevationProfileAbortError } from '@/lib/pointcloud/walkPointCloud';
export { PointCloudWalkError as ElevationProfileError } from '@/lib/pointcloud/walkPointCloud';

const DEFAULT_MAX_RAW_POINTS = 2_000_000;
const DEFAULT_MAX_CHART_SAMPLES = 5_000;

/**
 * Sentinel thrown to break out of the per-point loop once we've collected
 * `maxRawPoints` samples. The walker doesn't expose a "stop iterating" API
 * (its callback signature is `void`), so abuse-throw + catch is the
 * cleanest way to bail without iterating the rest of every node.
 */
class MaxRawPointsReached {}

export async function computeElevationProfile(
  input: ComputeProfileInput,
): Promise<ComputeProfileResult> {
  const { polyline, buffer, pco, onProgress, signal } = input;
  const maxRawPoints = input.maxRawPoints ?? DEFAULT_MAX_RAW_POINTS;
  const maxChartSamples = input.maxChartSamples ?? DEFAULT_MAX_CHART_SAMPLES;
  const maxConcurrentLoads = input.maxConcurrentLoads ?? 6;
  const targetSpacing = input.targetSpacing ?? input.buffer / 2;

  if (polyline.length < 2) {
    throw new PointCloudWalkError('Polyline must have at least 2 vertices');
  }
  if (!Number.isFinite(buffer) || buffer <= 0) {
    throw new PointCloudWalkError('Buffer must be positive');
  }

  const segments = buildSegments(polyline);
  if (segments.length === 0) {
    throw new PointCloudWalkError('Polyline has no non-degenerate segments');
  }
  const total = totalLength(segments);
  const aabb = polylineXyAabb(polyline, buffer);
  const bufferSq = buffer * buffer;

  const t0 = performance.now();

  const accepted: Array<{ chainage: number; elevation: number }> = [];
  // -1 sentinel = "walk was truncated by MaxRawPointsReached", so node-count
  // telemetry isn't meaningful. Real value comes from the walker's result.
  let nodesUsed = -1;

  try {
    const result = await walkPointCloud({
      pco,
      shouldVisitNode: (worldBox) =>
        worldBox.max.x >= aabb.minX &&
        worldBox.min.x <= aabb.maxX &&
        worldBox.max.y >= aabb.minY &&
        worldBox.min.y <= aabb.maxY,
      onPoint: (wx, wy, wz) => {
        // Cheap AABB cull first — most points fail this on a thin corridor
        // with a wide-AABB node, so the perp-distance projection is skipped.
        if (wx < aabb.minX || wx > aabb.maxX || wy < aabb.minY || wy > aabb.maxY) return;

        const proj = projectPointToPolyline(wx, wy, segments);
        if (!proj) return;
        // Squared compare — projection already does sqrt for the best, but
        // we pre-compare squared to avoid the sqrt-then-square round trip.
        if (proj.perpDist * proj.perpDist > bufferSq) return;

        accepted.push({ chainage: proj.chainage, elevation: wz });

        if (accepted.length >= maxRawPoints) {
          // Stop the walk — we've collected enough samples to downsample
          // from. See `MaxRawPointsReached` rationale above.
          throw new MaxRawPointsReached();
        }
      },
      onProgress: onProgress
        ? (phase, done, total) => {
            // Walker emits 'loading' | 'processing'. The elevation-profile
            // panel labels Phase 3 as "Sampling", so translate here.
            onProgress(phase === 'processing' ? 'sampling' : 'loading', done, total);
          }
        : undefined,
      signal,
      targetSpacing,
      maxConcurrentLoads,
    });
    nodesUsed = result.nodesUsed;
  } catch (e) {
    if (!(e instanceof MaxRawPointsReached)) throw e;
    // Truncated walk — keep `nodesUsed = -1` and use the partial samples.
  }

  // Sort by chainage, decimate to ≤ maxChartSamples.
  accepted.sort((a, b) => a.chainage - b.chainage);

  let chartSamples: Array<{ chainage: number; elevation: number }>;
  if (accepted.length <= maxChartSamples) {
    chartSamples = accepted;
  } else {
    // Stride decimation preserves shape and is O(n).
    const stride = accepted.length / maxChartSamples;
    chartSamples = new Array(maxChartSamples);
    for (let i = 0; i < maxChartSamples; i++) {
      chartSamples[i] = accepted[Math.min(accepted.length - 1, Math.floor(i * stride))];
    }
  }

  return {
    samples: chartSamples,
    rawPointCount: accepted.length,
    totalLength: total,
    nodesUsed,
    durationMs: performance.now() - t0,
  };
}
